import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Scope property nodes - a Notion-style two-column property panel rendered INSIDE the deal Scope
 * Tiptap doc for groups the host marks `groupLayoutHint: 'property-panel'` (Company Overview,
 * Primary Contact). Two nodes:
 *
 * - `propertyList` (block, content `propertyRow+`) - one panel per group. Its node view draws the
 *   per-panel "Show all / Show less" toggle that reveals the empty (collapsed) rows. The
 *   answered-vs-empty split is a RUNTIME decision (read live from the host api), so filling a value
 *   moves its row into the shown set without a doc rebuild - the row values are never baked into the
 *   doc JSON (only `fieldKey` is), so a live edit produces an identical doc and never resets the
 *   editor (RichTextEditor's content-sync deep-compares).
 * - `propertyRow` (block atom, attr `fieldKey`) - one property. Its node view renders the row's
 *   label + value control through the HOST-supplied `ScopePropertyApi` (the controls live in
 *   apps/web - they read questionnaire options + write answers through the same path as the
 *   Questionnaire tab, so the two tabs stay in sync). Same host-render-context pattern as
 *   `sowDataTableNode`, because the renderer lives outside this package.
 *
 * Neither node is harvested on Scope-doc Save: they carry no `field:` text anchor and no editable
 * text, and the baseline is re-projected through the same builder, so the diff for these groups is
 * always empty (values persist live via the controls, not via harvest).
 */
import * as React from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, } from '@tiptap/react';
const ScopePropertyContext = React.createContext(null);
export function ScopePropertyProvider({ api, children, }) {
    return _jsx(ScopePropertyContext.Provider, { value: api, children: children });
}
// ── propertyRow (atom) ───────────────────────────────────────────────────────
function PropertyRowNodeView({ node }) {
    const api = React.useContext(ScopePropertyContext);
    const fieldKey = String(node.attrs.fieldKey ?? '');
    const empty = api ? api.isRowEmpty(fieldKey) : false;
    return (_jsx(NodeViewWrapper, { as: "div", className: `scope-prop-row${empty ? ' scope-prop-row--empty' : ''}`, contentEditable: false, "data-field-key": fieldKey, children: api ? api.renderRow(fieldKey) : null }));
}
export const PropertyRowNode = Node.create({
    name: 'propertyRow',
    group: 'block',
    atom: true,
    selectable: false,
    draggable: false,
    addAttributes() {
        return {
            fieldKey: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-field-key') ?? '',
            },
        };
    },
    parseHTML() {
        return [{ tag: 'div[data-property-row]' }];
    },
    renderHTML({ node, HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-property-row': '',
                'data-field-key': String(node.attrs.fieldKey ?? ''),
            }),
        ];
    },
    addNodeView() {
        return ReactNodeViewRenderer(PropertyRowNodeView);
    },
});
// ── propertyList (container) ─────────────────────────────────────────────────
function PropertyListNodeView({ node }) {
    const api = React.useContext(ScopePropertyContext);
    const [expanded, setExpanded] = React.useState(false);
    // Count empty (collapsed) rows live from the host api so the toggle label + visibility react to
    // answers changing under the panel (fill a value -> its row leaves the collapsed set).
    let emptyCount = 0;
    if (api) {
        node.forEach((child) => {
            const fk = String(child.attrs.fieldKey ?? '');
            if (fk && api.isRowEmpty(fk))
                emptyCount += 1;
        });
    }
    return (_jsxs(NodeViewWrapper, { as: "div", className: `scope-prop-list${expanded ? ' is-expanded' : ''}`, children: [_jsx(NodeViewContent, { className: "scope-prop-list__rows" }), emptyCount > 0 ? (_jsx("div", { className: "scope-prop-list__footer", contentEditable: false, children: _jsx("button", { type: "button", className: "scope-prop-list__toggle", 
                    // preventDefault on mousedown so clicking the toggle never steals the editor selection.
                    onMouseDown: (e) => e.preventDefault(), onClick: () => setExpanded((v) => !v), children: expanded ? 'Show less' : `Show all (${emptyCount})` }) })) : null] }));
}
export const PropertyListNode = Node.create({
    name: 'propertyList',
    group: 'block',
    content: 'propertyRow+',
    selectable: false,
    draggable: false,
    parseHTML() {
        return [{ tag: 'div[data-property-list]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-property-list': '' }), 0];
    },
    addNodeView() {
        return ReactNodeViewRenderer(PropertyListNodeView);
    },
});
//# sourceMappingURL=scopePropertyNode.js.map